import React, { useState, useEffect } from 'react'; 
import { useAdminContext } from '../../context/AdminContext.js';

const CrearAcuerdoManual = () => {
    const { users, handleAdminCreateAgreement, processingId } = useAdminContext();

    const safeLower = (value = '') => (value ?? '').toString().toLowerCase();
    const normalizedUsers = Array.isArray(users) ? users : [];
    
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    const [amount, setAmount] = useState('');
    const [activateSmallOwner, setActivateSmallOwner] = useState(false);   
    const [activateAccelerator, setActivateAccelerator] = useState(false);     
    const [isTest, setIsTest] = useState(false); // <-- NUEVO ESTADO
    const [notes, setNotes] = useState('');
    const [allowTestCommissions, setAllowTestCommissions] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (selectedUser?.isSmallOwner) {
            setActivateSmallOwner(true);
        } else {
            setActivateSmallOwner(false);
        }
    }, [selectedUser]);

    const handleUserSelect = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        const lowerValue = safeLower(value);
        
        // Búsqueda más robusta por múltiples campos de email o ID exacto
        const userFound = normalizedUsers.find(u => 
            safeLower(u?.userEmail) === lowerValue || 
            safeLower(u?.email) === lowerValue || 
            u?.id === value
        );

        if (userFound) {
            setSelectedUser(userFound);
            setError(''); // Limpiar error si se encuentra un usuario
        } else {
            setSelectedUser(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!selectedUser) {
            setError('Debes seleccionar un usuario válido de la lista.');
            return;
        }

        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            setError('Debes ingresar un monto válido y mayor a cero.');
            return;
        }
        
        const shouldActivateOwner = selectedUser?.isSmallOwner ? false : activateSmallOwner;
        // --- CORRECCIÓN: Pasar el nuevo estado 'isTest' a la función ---
        try {
            await handleAdminCreateAgreement(
                selectedUser.id,
                parsedAmount,
                shouldActivateOwner,
                activateAccelerator,
                notes,
                isTest,
                allowTestCommissions
            );
        } catch (err) {
            console.error("Error al crear acuerdo:", err);
            setError(`Error: ${err.message || 'No se pudo conectar con el servidor'}`);
            return; // Detener la limpieza del formulario para que el admin pueda corregir
        }
        
        setSearchTerm('');
        setSelectedUser(null);
        setAmount('');
        setIsTest(false); // <-- Limpiar estado
        setAllowTestCommissions(false);
        setActivateSmallOwner(false);
        setActivateAccelerator(false);
        setNotes('');
    };

    useEffect(() => {
        if (!isTest && allowTestCommissions) {
            setAllowTestCommissions(false);
        }
    }, [isTest, allowTestCommissions]);

    const isProcessing = processingId && processingId.startsWith('create-agreement');

    return (
        <div className="main-panel">
            <h2 className="section-title">Crear Acuerdo Manual</h2>
            <p>Esta herramienta crea un nuevo acuerdo aprobado para un usuario existente. Úsala para registrar bonos, premios o inversiones realizadas por fuera de la plataforma.</p>
            
            <div className="admin-utilities-card" style={{ marginTop: '1.5rem', padding: '1.5rem', maxWidth: '600px', margin: '1.5rem auto' }}>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    
                    <div>
                        <label htmlFor="user-search">Buscar Usuario por Email o Nombre</label>
                        <input
                            id="user-search"
                            type="text"
                            list="user-suggestions"
                            placeholder="Escribe para buscar..."
                            value={searchTerm}
                            onChange={handleUserSelect}
                            required
                            style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                        />
                        <datalist id="user-suggestions">
                            {normalizedUsers
                                .filter(user => {
                                    const term = safeLower(searchTerm);
                                    if (!term) return false; // No mostrar sugerencias si el campo está vacío
                                    if (!user) return false;                                    
                                    return (
                                        safeLower(user?.fullName).includes(term) || 
                                        safeLower(user?.userEmail || user?.email).includes(term) ||
                                        user?.id === term
                                    );
                                })
                                .slice(0, 10) // Mostrar solo los primeros 10 resultados para mejorar el rendimiento
                                .map(user => (
                                <option key={user?.id || Math.random()} value={user?.userEmail || user?.email || user?.id}>
                                    {user?.fullName || user?.username || 'Usuario sin nombre'}
                                </option>
                            ))}
                        </datalist>
                        {selectedUser && (
                            <p style={{color: 'green', fontSize: '0.9em', marginTop: '4px'}}>
                                <strong>Seleccionado:</strong> {selectedUser.fullName || selectedUser.userEmail} <br/>
                                <small>UID: {selectedUser.id}</small>
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="agreement-amount">Monto del Acuerdo ($)</label>
                        <input
                            id="agreement-amount"
                            type="number"
                            placeholder="Ej: 500.00"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="agreement-notes">Red / Notas (Opcional)</label>
                        <input
                            id="agreement-notes"
                            type="text"
                            placeholder="Ej: TRC20, Pago manual, Bono..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            style={{ width: '100%', padding: '8px', marginTop: '4px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                            id="activate-small-owner"
                            type="checkbox"
                            checked={activateSmallOwner}
                            onChange={(e) => setActivateSmallOwner(e.target.checked)}
                            disabled={selectedUser?.isSmallOwner}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <label htmlFor="activate-small-owner">
                            {selectedUser?.isSmallOwner 
                                ? 'El usuario ya es "Pequeño Dueño"' 
                                : 'Activar estatus de "Pequeño Dueño"'}
                        </label>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                            id="activate-accelerator"
                            type="checkbox"
                            checked={activateAccelerator}
                            onChange={(e) => setActivateAccelerator(e.target.checked)}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <label htmlFor="activate-accelerator">
                            Activar <strong>Acelerador</strong> (se descontará 5%)
                        </label>
                    </div>

                    {/* --- NUEVO CHECKBOX PARA ACUERDOS DE PROMOCIÓN --- */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                            id="is-test-agreement"
                            type="checkbox"
                            checked={isTest}
                            onChange={(e) => setIsTest(e.target.checked)}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <label htmlFor="is-test-agreement">
                            Marcar si es un acuerdo de <strong>Promoción</strong>
                        </label>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', opacity: isTest ? 1 : 0.5 }}>
                        <input
                            id="allow-test-commissions"
                            type="checkbox"
                            checked={allowTestCommissions}
                            onChange={(e) => setAllowTestCommissions(e.target.checked)}
                            disabled={!isTest}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <label htmlFor="allow-test-commissions">
                            Permitir pago de comisiones aun siendo promoción
                        </label>
                    </div>
                    
                    {error && <p className="message-box error" style={{ margin: '1rem 0 0 0' }}>{error}</p>}

                    <button type="submit" className="login-button" disabled={isProcessing}>
                        {isProcessing ? 'Creando Acuerdo...' : 'Crear Acuerdo Manual'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CrearAcuerdoManual;